import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import authService from '../../services/authService';

const ResetPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [isSuccess, setIsSuccess] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm();

  const password = watch('password');

  // Extract parameters from URL (both query params and path params)
  useEffect(() => {
    let email, type, time;
    
    // Try to get from path parameters first
    if (params.email && params.type && params.time) {
      email = decodeURIComponent(params.email);
      type = decodeURIComponent(params.type);
      time = params.time;
    } else {
      // Fallback to query parameters
      email = searchParams.get('email');
      type = searchParams.get('type');
      time = searchParams.get('time');
    }
    
    if (email && type && time) {
      // Call API to decrypt the email and validate the link
      const validateResetLink = async () => {
        try {
          const response = await authService.getResetPasswordData(email, type, time);
          if (response.status === 1) {
            setValue('email', response.data.email);
            setValue('type', response.data.type);
            setValue('time', time);
            setMessage('Valid reset link. Please enter your new password.');
            setMessageType('success');
          } else {
            setMessage(response.message || 'Invalid reset link. Please request a new password reset.');
            setMessageType('error');
          }
        } catch (error) {
          console.error('Reset link validation error:', error);
          setMessage('Invalid reset link. Please request a new password reset.');
          setMessageType('error');
        }
      };
      
      validateResetLink();
    } else {
      setMessage('Invalid reset link. Please request a new password reset.');
      setMessageType('error');
    }
  }, [searchParams, params, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await authService.resetPassword(
        data.email,
        data.password,
        data.password_confirmation,
        data.type || 'user'
      );
      
      if (response.status === 1) {
        setMessage(response.message);
        setMessageType('success');
        setIsSuccess(true);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login-page');
        }, 3000);
      } else {
        setMessage(response.message || 'Password reset failed. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setMessage(
        error.response?.data?.message || 
        'An error occurred while resetting your password. Please try again.'
      );
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login-page');
  };

  if (isSuccess) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 64, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h4" gutterBottom color="success.main">
            Password Reset Successful!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your password has been successfully updated. You will be redirected to the login page shortly.
          </Typography>
          <Button
            variant="contained"
            onClick={handleBackToLogin}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockIcon 
              sx={{ 
                fontSize: 48, 
                color: 'primary.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h4" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your new password below
            </Typography>
          </Box>

          {message && (
            <Alert 
              severity={messageType} 
              sx={{ mb: 3 }}
            >
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password_confirmation', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === password || 'Passwords do not match',
              })}
              error={!!errors.password_confirmation}
              helperText={errors.password_confirmation?.message}
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                Back to Login
              </Button>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <LockIcon />}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ResetPassword;